import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GitFork, FolderKanban, Waves, Building2 } from 'lucide-react';
import { aiAPI } from '../../services/api';
import { scopeLabelClass } from './chatViewTheme';
import { inputCompact } from '../ui/uiTheme';

export default function InlineScopePicker({ onSelect }: any) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef<any>(null);
  const abortRef = useRef<any>(null);

  const search = useCallback((q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setError('');
      setLoading(true);
      try {
        const data = await aiAPI.scopeCatalog(
          { q: q.trim(), limit: q.trim() ? 15 : 20 },
          controller.signal
        );
        if (abortRef.current === controller) {
          setResults(data.items || data || []);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setResults([]);
        setError((err as Error).message || 'Failed to load scope options');
      } finally {
        if (abortRef.current === controller) {
          setLoading(false);
        }
      }
    }, 200);
  }, []);

  useEffect(() => {
    search('');
    return () => {
      clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [search]);

  const iconFor = (kind) => {
    if (kind === 'repository') return <GitFork className="h-3.5 w-3.5 shrink-0" />;
    if (kind === 'project') return <FolderKanban className="h-3.5 w-3.5 shrink-0" />;
    if (kind === 'stream') return <Waves className="h-3.5 w-3.5 shrink-0" />;
    return <Building2 className="h-3.5 w-3.5 shrink-0" />;
  };

  return (
    <div className="mt-3 rounded-xl border border-brand-purple/30 bg-dark-elevated p-3">
      <p className={`mb-2 ${scopeLabelClass} font-semibold text-brand-purple-light`}>
        Select a scope to continue
      </p>
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          search(e.target.value);
        }}
        placeholder="Search repositories, projects, streams…"
        className={`${inputCompact} mb-2 w-full py-1.5 text-xs`}
        autoFocus
      />
      {loading && <p className="py-1 text-xs text-slate-500">Searching…</p>}
      {error && !loading && <p className="py-1 text-xs text-red-400">{error}</p>}
      <div className="max-h-48 space-y-0.5 overflow-y-auto">
        {results.map((item) => (
          <button
            key={`${item.kind}-${item.id}`}
            type="button"
            onClick={() => onSelect(item)}
            className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium text-slate-300 transition-colors hover:bg-brand-purple/10 hover:text-brand-purple-light"
          >
            <span className="text-brand-purple-light">{iconFor(item.kind)}</span>
            <span className="flex-1 truncate">{item.label || item.name}</span>
            <span className="shrink-0 text-[10px] capitalize text-slate-500">{item.kind}</span>
          </button>
        ))}
        {!loading && !error && results.length === 0 && (
          <p className="py-2 text-center text-xs text-slate-500">No results</p>
        )}
      </div>
    </div>
  );
}
